/**
 * Print sheet made in the browser from the result JPEG: the user picks the paper and the number of copies.
 * Same geometry as the render machine's own sheet (idphoto/printing/layout.py, pdf.py; render_worker/render.py):
 * photos at their real size in millimetres, 3 mm margins, a centred grid counted with a 0.34 mm gap that then widens
 * up to 3 mm while the paper has room (cutting room), and cut marks that never touch a photo (`cutMarks`).
 * tests/test_sheet_geometry.py writes sheet.geometry.json from the Python code; sheet.test.ts checks this file
 * against it. The JPEG is embedded as is (DCTDecode): no re-compression, the PDF is barely larger than the photo.
 */

export interface Paper {
  key: string;
  label: string;
  width_mm: number;
  height_mm: number;
}

/** Used when the server's catalogue does not list papers yet (same list as idphoto.core.standards.PAPER_SPECS). */
export const DEFAULT_PAPERS: Paper[] = [
  { key: "photo_10x15", label: "10 x 15 cm", width_mm: 100, height_mm: 150 },
  { key: "photo_13x18", label: "13 x 18 cm", width_mm: 130, height_mm: 180 },
  { key: "photo_20x30", label: "20 x 30 cm", width_mm: 200, height_mm: 300 },
  { key: "a6", label: "A6 - 105 x 148 mm", width_mm: 105, height_mm: 148 },
  { key: "a5", label: "A5 - 148 x 210 mm", width_mm: 148, height_mm: 210 },
  { key: "a4", label: "A4 - 210 x 297 mm", width_mm: 210, height_mm: 297 },
  { key: "letter", label: "Letter - 216 x 279 mm", width_mm: 216, height_mm: 279 },
  { key: "photo_4x6in", label: "4 x 6 inch", width_mm: 101.6, height_mm: 152.4 },
  { key: "photo_5x7in", label: "5 x 7 inch", width_mm: 127, height_mm: 177.8 },
];

export const SHEET_MARGIN_MM = 3;
export const GAP_MM = 0.34;
/** The ID-photo sheet's gaps widen up to this while the paper has room (render_worker/render.py SHEET_MAX_GAP_MM). */
export const MAX_GAP_MM = 3;
/** Longest cut mark (idphoto/printing/layout.py CUT_MARK_MM). */
export const CUT_MARK_MM = 3;
/** A mark starts this far beyond its photo's corner and keeps this far from any other photo (CUT_MARK_OFFSET_MM). */
export const CUT_MARK_OFFSET_MM = 0.5;
/** What is left of a mark in a narrower gap is not drawn: that line is marked in the margin (CUT_MARK_MIN_MM). */
export const CUT_MARK_MIN_MM = 0.8;
const EPS = 1e-6;

export interface Cell {
  x: number; // mm from the left edge of the paper
  y: number; // mm from the top edge
  w: number;
  h: number;
}

// ---- the grid of both modes' print sheets (this file for "Ảnh thẻ", editor/printLayout.ts for "Xếp tờ in") ----------

/** Most photos of w × h (mm) in a usable area with `gap` between them: [columns, rows]. */
export function fitGrid(usableW: number, usableH: number, w: number, h: number, gap: number): [number, number] {
  if (w <= 0 || h <= 0) return [0, 0];
  return [Math.max(0, Math.floor((usableW + gap + EPS) / (w + gap))), Math.max(0, Math.floor((usableH + gap + EPS) / (h + gap)))];
}

/**
 * The most compact cols x rows grid that holds `n` photos, shaped like the usable area (calculate_smart_grid of the
 * desktop layout): 6 photos on A4 become 2 x 3, not a full row of 5 and one left over.
 */
export function compactGrid(usableW: number, usableH: number, w: number, h: number, gap: number, n: number): [number, number] {
  const [maxCols, maxRows] = fitGrid(usableW, usableH, w, h, gap);
  if (n >= maxCols * maxRows) return [maxCols, maxRows];
  const usableRatio = usableW / usableH;
  let best: [number, number, number] | null = null; // score, cols, rows
  for (let rows = 1; rows <= maxRows; rows++) {
    for (let cols = 1; cols <= maxCols; cols++) {
      if (cols * rows < n) continue;
      const bw = cols * w + (cols - 1) * gap;
      const bh = rows * h + (rows - 1) * gap;
      const score = (cols * rows - n) * 100000 + Math.abs(bw / bh - usableRatio) * 1000 + (usableW * usableH - bw * bh);
      if (!best || score < best[0]) best = [score, cols, rows];
    }
  }
  return best ? [best[1], best[2]] : [maxCols, maxRows];
}

/**
 * The gap between `count` photos taking `used` mm of `usable` mm: `gap`, widened up to `maxGap` while there is room,
 * never below `gap` (the photos were counted with it). idphoto/printing/layout.py spread_gap.
 */
export function spreadGap(usable: number, used: number, count: number, gap: number, maxGap: number): number {
  if (count < 2 || maxGap <= gap) return gap;
  return Math.max(gap, Math.min(maxGap, (usable - used) / (count - 1)));
}

/**
 * `n` cells of w × h in that compact grid, centred in the usable area (margins on every side), row by row. The grid
 * is counted with `gap`; its gaps then widen up to `maxGap` (default: no widening, the editor's own gap).
 */
export function gridCells(paperW: number, paperH: number, margin: number, gap: number, w: number, h: number,
                          n: number, maxGap = gap): Cell[] {
  const usableW = paperW - 2 * margin;
  const usableH = paperH - 2 * margin;
  const [cols, rows] = compactGrid(usableW, usableH, w, h, gap, n);
  if (cols === 0 || rows === 0) return [];
  const gx = spreadGap(usableW, cols * w, cols, gap, maxGap);
  const gy = spreadGap(usableH, rows * h, rows, gap, maxGap);
  const x0 = margin + Math.max(0, (usableW - (cols * w + (cols - 1) * gx)) / 2);
  const y0 = margin + Math.max(0, (usableH - (rows * h + (rows - 1) * gy)) / 2);
  const cells: Cell[] = [];
  for (let i = 0; i < n; i++) {
    cells.push({ x: x0 + (i % cols) * (w + gx), y: y0 + Math.floor(i / cols) * (h + gy), w, h });
  }
  return cells;
}

/** Most photos of `photo` size (mm) that fit one sheet: [columns, rows]. */
export function grid(paper: Paper, photoW: number, photoH: number): [number, number] {
  return fitGrid(paper.width_mm - 2 * SHEET_MARGIN_MM, paper.height_mm - 2 * SHEET_MARGIN_MM, photoW, photoH, GAP_MM);
}

export function capacity(paper: Paper, photoW: number, photoH: number): number {
  const [cols, rows] = grid(paper, photoW, photoH);
  return cols * rows;
}

/** A cell of a sheet with photos of several specs (GĐ4): `image` = which photo goes in it. */
export interface PlacedCell extends Cell {
  image: number;
}

/**
 * GĐ4 "tờ in trộn quy cách": each spec on rows of its own (4 × 3x4, then 2 × 4x6 …), left to right, a new row when
 * the next photo does not fit; null when they do not all fit one sheet. What fits is counted with the 0.34 mm gap;
 * then, as on the one-spec sheet, the gaps widen up to MAX_GAP_MM while the paper has room: the rows of one spec
 * share the gap its fullest row leaves (its columns stay in line) and are centred across the paper; every row gap is
 * the same and the rows are centred down it.
 */
export function mixedLayout(paper: Paper, items: { w: number; h: number; copies: number }[]): PlacedCell[] | null {
  const right = paper.width_mm - SHEET_MARGIN_MM;
  const bottom = paper.height_mm - SHEET_MARGIN_MM;
  const usableW = right - SHEET_MARGIN_MM;
  const usableH = bottom - SHEET_MARGIN_MM;
  // 1. which row and column each photo takes, with the smallest gap (as many photos per sheet as ever)
  const placed: { image: number; w: number; h: number; row: number; col: number }[] = [];
  let x = SHEET_MARGIN_MM;
  let y = SHEET_MARGIN_MM;
  let rowH = 0;
  let row = 0;
  let col = 0;
  const newRow = () => {
    x = SHEET_MARGIN_MM;
    y += rowH + GAP_MM;
    rowH = 0;
    row += 1;
    col = 0;
  };
  for (const [image, it] of items.entries()) {
    if (x > SHEET_MARGIN_MM) newRow();                 // every spec starts a row of its own: cut lines stay straight
    for (let i = 0; i < it.copies; i++) {
      if (x + it.w > right + EPS) newRow();
      if (x + it.w > right + EPS || y + it.h > bottom + EPS) return null;
      placed.push({ image, w: it.w, h: it.h, row, col });
      x += it.w + GAP_MM;
      rowH = Math.max(rowH, it.h);
      col += 1;
    }
  }
  if (!placed.length) return [];
  // 2. the room left widens the gaps
  const rowIds = [...new Set(placed.map((p) => p.row))];
  const heights = rowIds.map((r) => Math.max(...placed.filter((p) => p.row === r).map((p) => p.h)));
  const total = heights.reduce((a, b) => a + b, 0);
  const gy = spreadGap(usableH, total, rowIds.length, GAP_MM, MAX_GAP_MM);
  const rowTop = new Map<number, number>();
  let top = SHEET_MARGIN_MM + Math.max(0, (usableH - (total + (rowIds.length - 1) * gy)) / 2);
  rowIds.forEach((r, i) => {
    rowTop.set(r, top);
    top += heights[i] + gy;
  });
  const columns = new Map<number, { left: number; step: number }>();
  for (const image of new Set(placed.map((p) => p.image))) {
    const mine = placed.filter((p) => p.image === image);
    const perRow = Math.max(...mine.map((p) => p.col)) + 1;
    const w = mine[0].w;
    const gx = spreadGap(usableW, perRow * w, perRow, GAP_MM, MAX_GAP_MM);
    columns.set(image, { left: SHEET_MARGIN_MM + Math.max(0, (usableW - (perRow * w + (perRow - 1) * gx)) / 2),
                         step: w + gx });
  }
  return placed.map((p) => {
    const c = columns.get(p.image)!;
    return { x: c.left + p.col * c.step, y: rowTop.get(p.row)!, w: p.w, h: p.h, image: p.image };
  });
}

/**
 * `copies` cells (clamped to what fits) in a compact grid centred on the paper, filled row by row, gaps widened up
 * to MAX_GAP_MM (the render machine's sheet_pdf).
 */
export function layout(paper: Paper, photoW: number, photoH: number, copies: number): Cell[] {
  const n = Math.min(Math.max(1, Math.floor(copies)), capacity(paper, photoW, photoH));
  if (n <= 0) return [];
  return gridCells(paper.width_mm, paper.height_mm, SHEET_MARGIN_MM, GAP_MM, photoW, photoH, n, MAX_GAP_MM);
}

/** A cut mark: x1, y1, x2, y2 in mm from the paper's top-left corner (x1 <= x2, y1 <= y2). */
export type Segment = [number, number, number, number];

type Obstacle = [number, number, number, number]; // along from, along to, across from, across to

/** (from, to) along the line `at` of the mark leaving a corner at `edge` in `direction` (-1 / +1), or null. */
function markSpan(edge: number, direction: number, at: number, obstacles: Obstacle[], limit: number): [number, number] | null {
  const keep = CUT_MARK_OFFSET_MM;
  const start = edge + direction * keep;
  let end = edge + direction * (keep + CUT_MARK_MM);
  for (const [a0, a1, c0, c1] of obstacles) {
    if (!(c0 - keep + EPS < at && at < c1 + keep - EPS)) continue;   // the line passes this photo at least `keep` away
    const lo = a0 - keep;
    const hi = a1 + keep;
    if (lo + EPS < start && start < hi - EPS) return null;          // the gap is too narrow for a mark here
    if (direction > 0) {
      if (start <= lo + EPS) end = Math.min(end, lo);
    } else if (start >= hi - EPS) {
      end = Math.max(end, hi);
    }
  }
  end = Math.min(Math.max(end, 0), limit);
  if ((end - start) * direction < CUT_MARK_MIN_MM - EPS) return null;
  return [Math.min(start, end), Math.max(start, end)];
}

/** Collinear marks (lines within EPS) that overlap or touch become one; sorted by (line, from). */
function mergeMarks(marks: [number, number, number][]): [number, number, number][] {
  const sorted = [...marks].sort((p, q) => p[0] - q[0] || p[1] - q[1] || p[2] - q[2]);
  const lines: [number, number, number][][] = [];
  for (const m of sorted) {
    const line = lines[lines.length - 1];
    if (line && m[0] - line[0][0] <= EPS) line.push(m);
    else lines.push([m]);
  }
  const merged: [number, number, number][] = [];
  for (const line of lines) {
    const at = line[0][0];
    const spans = line.map(([, a, b]) => [a, b]).sort((p, q) => p[0] - q[0] || p[1] - q[1]);
    let [lo, hi] = spans[0];
    for (const [a, b] of spans.slice(1)) {
      if (a <= hi + EPS) {
        hi = Math.max(hi, b);
      } else {
        merged.push([at, lo, hi]);
        [lo, hi] = [a, b];
      }
    }
    merged.push([at, lo, hi]);
  }
  return merged;
}

/**
 * Cut marks that never touch a photo (idphoto/printing/layout.py cut_mark_segments): each corner of each photo gets
 * two marks on the extension of its edges, pointing away from it, from CUT_MARK_OFFSET_MM beyond the corner, at most
 * CUT_MARK_MM long, stopped CUT_MARK_OFFSET_MM short of any other photo and at the paper edge; what is left under
 * CUT_MARK_MIN_MM is dropped (a gap that narrow is marked in the margins only). Horizontal marks by (y, x), then
 * vertical ones by (x, y).
 */
export function cutMarks(cells: Cell[], paperW: number, paperH: number): Segment[] {
  const rects = cells.map((c) => [c.x, c.y, c.x + c.w, c.y + c.h]);
  const horizontal: [number, number, number][] = [];
  const vertical: [number, number, number][] = [];
  rects.forEach(([x0, y0, x1, y1], i) => {
    const others = rects.filter((_, j) => j !== i);
    const alongX: Obstacle[] = others.map((r) => [r[0], r[2], r[1], r[3]]);
    const alongY: Obstacle[] = others.map((r) => [r[1], r[3], r[0], r[2]]);
    for (const y of [y0, y1]) {
      for (const [x, d] of [[x0, -1], [x1, 1]]) {
        const span = markSpan(x, d, y, alongX, paperW);
        if (span) horizontal.push([y, ...span]);
      }
    }
    for (const x of [x0, x1]) {
      for (const [y, d] of [[y0, -1], [y1, 1]]) {
        const span = markSpan(y, d, x, alongY, paperH);
        if (span) vertical.push([x, ...span]);
      }
    }
  });
  return [...mergeMarks(horizontal).map(([at, a, b]): Segment => [a, at, b, at]),
          ...mergeMarks(vertical).map(([at, a, b]): Segment => [at, a, at, b])];
}

export interface JpegInfo {
  width: number;
  height: number;
  components: number; // 1 gray, 3 RGB / YCbCr, 4 CMYK
}

/** Size and colour components from the JPEG's start-of-frame marker; null when it is not a baseline/progressive JPEG. */
export function jpegInfo(bytes: Uint8Array): JpegInfo | null {
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  let i = 2;
  while (i + 9 < bytes.length) {
    if (bytes[i] !== 0xff) return null;
    const marker = bytes[i + 1];
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      i += 2;
      continue;
    }
    const len = (bytes[i + 2] << 8) | bytes[i + 3];
    const isSof = marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
    if (isSof) {
      return { height: (bytes[i + 5] << 8) | bytes[i + 6], width: (bytes[i + 7] << 8) | bytes[i + 8], components: bytes[i + 9] };
    }
    i += 2 + len;
  }
  return null;
}

const PT_PER_MM = 72 / 25.4;
const num = (v: number) => (Math.round(v * 100) / 100).toString();

/** One-page PDF: the JPEG placed in every cell (mm, top-left origin) with cut marks. */
export function sheetPdf(jpeg: Uint8Array, paper: Paper, cells: Cell[]): Uint8Array {
  return sheetPdfMulti([jpeg], paper, cells.map((c) => ({ ...c, image: 0 })));
}

/** GĐ4: one-page PDF with several JPEGs (one per spec), each cell showing `jpegs[cell.image]`. */
export function sheetPdfMulti(jpegs: Uint8Array[], paper: Paper, cells: PlacedCell[]): Uint8Array {
  const infos = jpegs.map((j) => {
    const info = jpegInfo(j);
    if (!info) throw new Error("not a JPEG");
    return info;
  });
  const pageW = paper.width_mm * PT_PER_MM;
  const pageH = paper.height_mm * PT_PER_MM;

  const ops: string[] = [];
  for (const c of cells) {
    const x = c.x * PT_PER_MM;
    const w = c.w * PT_PER_MM;
    const h = c.h * PT_PER_MM;
    const y = pageH - (c.y * PT_PER_MM + h); // PDF origin is bottom-left
    ops.push(`q ${num(w)} 0 0 ${num(h)} ${num(x)} ${num(y)} cm /Im${c.image + 1} Do Q`);
  }
  ops.push("0.25 w 0 0 0 RG");
  for (const [x1, y1, x2, y2] of cutMarks(cells, paper.width_mm, paper.height_mm)) {
    ops.push(`${num(x1 * PT_PER_MM)} ${num(pageH - y1 * PT_PER_MM)} m ${num(x2 * PT_PER_MM)} ${num(pageH - y2 * PT_PER_MM)} l S`);
  }
  const content = ops.join("\n");

  const enc = new TextEncoder();
  const parts: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;
  const push = (chunk: Uint8Array | string) => {
    const bytes = typeof chunk === "string" ? enc.encode(chunk) : chunk;
    parts.push(bytes);
    length += bytes.length;
  };
  const object = (id: number, body: string) => {
    offsets[id] = length;
    push(`${id} 0 obj\n${body}\nendobj\n`);
  };

  // objects: 1 catalog, 2 pages, 3 page, 4 … 3+n the images, 4+n the content stream
  const contentsId = 4 + jpegs.length;
  const xobjects = jpegs.map((_, i) => `/Im${i + 1} ${4 + i} 0 R`).join(" ");
  push("%PDF-1.4\n%âãÏÓ\n");
  object(1, "<< /Type /Catalog /Pages 2 0 R >>");
  object(2, "<< /Type /Pages /Kids [3 0 R] /Count 1 >>");
  object(3, `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${num(pageW)} ${num(pageH)}] ` +
            `/Resources << /XObject << ${xobjects} >> >> /Contents ${contentsId} 0 R >>`);
  jpegs.forEach((jpeg, i) => {
    const info = infos[i];
    const colorSpace = info.components === 1 ? "/DeviceGray"
      : info.components === 4 ? "/DeviceCMYK /Decode [1 0 1 0 1 0 1 0]" : "/DeviceRGB";
    offsets[4 + i] = length;
    push(`${4 + i} 0 obj\n<< /Type /XObject /Subtype /Image /Width ${info.width} /Height ${info.height} /ColorSpace ${colorSpace} ` +
         `/BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`);
    push(jpeg);
    push("\nendstream\nendobj\n");
  });
  object(contentsId, `<< /Length ${enc.encode(content).length} >>\nstream\n${content}\nendstream`);
  const xref = length;
  const ids = Array.from({ length: contentsId }, (_, i) => i + 1);
  const rows = ids.map((id) => `${String(offsets[id]).padStart(10, "0")} 00000 n \n`).join("");
  push(`xref\n0 ${contentsId + 1}\n0000000000 65535 f \n${rows}trailer\n<< /Size ${contentsId + 1} /Root 1 0 R >>\n` +
       `startxref\n${xref}\n%%EOF\n`);

  const out = new Uint8Array(length);
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}
